import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Layout } from '../../layout'
import {
  Badge,
  Button,
  Container,
  DotsMenu,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Pagination,
  Popover,
  Row,
  useServerPagination
} from 'react-angle-dashboard-components'
import DatePicker from 'react-datepicker'
import { useHistory } from 'react-router-dom'
import { Paths } from '../../routes/paths'
import { Accordion, Table } from 'react-bootstrap'
import { quoteService } from '../../services'
import { IGroupQueryParams, IQuoteGroupDTO } from '../../services/quote/dto'
import GroupFormModal from '../../components/GroupFormModal/GroupFormModal'
import filterIcon from './filterIcon.svg'
import sortIcon from './sortIcon.svg'
import FTUimg from './FTUimg.png'
import { useDebounce } from 'react-angle-dashboard-components'

import 'react-datepicker/dist/react-datepicker.css'
import './Quotes.css'

const Quotes = () => {
  const history = useHistory()

  const onNewQuoteClick = () => {
    history.push(Paths.quoteForm())
  }

  const onViewQuoteClick = (quoteId: string, isDraft: boolean) => {
    if (isDraft) {
      history.push(Paths.quoteFormQuoteReview(quoteId))
    } else {
      history.push(Paths.viewQuote(quoteId))
    }
  }

  const quoteStatusStyles = {
    height: '8px',
    width: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '7px'
  }

  const quoteEnums = {
    quoteNumber: 'QUOTE NUMBER',
    quoteDate: 'QUOTE DATE'
  }

  const sortDirectionEnums = {
    asc: 'asc',
    desc: 'desc'
  }

  const filterEnums = {
    agencyName: 'Agency Name',
    agencyOffice: 'Agency Office',
    brokerName: 'Broker Name'
  }

  const groupEnums = {
    groupName: 'GROUP NAME',
    effectiveDate: 'EFFECTIVE DATE',
    quotes: 'QUOTES',
    members: 'MEMBERS',
    lastQuoted: 'LAST QUOTED',
    quoteStatus: 'QUOTE STATUS',
    agencyName: filterEnums.agencyName.toUpperCase(),
    agencyOffice: filterEnums.agencyOffice.toUpperCase(),
    brokerName: filterEnums.brokerName.toUpperCase(),
  }

  const [ordering, setOrdering] = useState({
    column: '',
    direction: ''
  })
  const [groupNameFilter, setGroupNameFilter] = useState('')
  const [effectiveDateGteFilter, setEffectiveDateGteFilter] =
    useState<Date | null>(null)
  const [effectiveDateLteFilter, setEffectiveDateLteFilter] =
    useState<Date | null>(null)
  const [lastQuoteDateGteFilter, setLastQuoteDateGteFilter] =
    useState<Date | null>(null)
  const [lastQuoteDateLteFilter, setLastQuoteDateLteFilter] =
    useState<Date | null>(null)
  const [stateFilter, setStateFilter] = useState('')
  // const [lastQuoteDateFilter, setLastQuoteDateFilter] = useState('')
  const [lastQuoteStatusFilter, setLastQuoteStatusFilter] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const deboucedEmailFilter = useDebounce(emailFilter, 1000)
  const [agencyFilter, setAgencyFilter] = useState<number | undefined>(undefined)
  const [brokerFilter, setBrokerFilter] = useState<number | undefined>(undefined)
  const [officeFilter, setOfficeFilter] = useState<number | undefined>(undefined)

  const [showFTUModal, setShowFTUModal] = useState(false)
  const [showFilterFormPopover, setShowFilterFormPopover] = useState(false)

  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [quotesSortBy, setQuotesSortBy] = useState<string>(quoteEnums.quoteDate)
  const [quotesSortDirection, setQuotesSortDirection] = useState<string>(
    sortDirectionEnums.desc
  )

  const [isGroupFormModalOpen, setIsGroupFormModalOpen] = useState(false)
  const [groupIdToEdit, setGroupIdToEdit] = useState<number | undefined>(
    undefined
  )

  const { data: options } = useQuery(
    ['state-choices'],
    quoteService.getStateChoices
  )

  const { data: agencies } = useQuery(
    ['agency-choices'],
    quoteService.getAgencies
  )

  const { data: brokers } = useQuery(
    ['broker-choices'],
    quoteService.getBrokers
  )

  const { data: offices } = useQuery(
    ['office-choices'],
    quoteService.getOffices
  )

  const { paginationConfig, queryResult } = useServerPagination(
    [
      'quotes.groups',
      `${ordering.direction}${ordering.column}`,
      ordering,
      groupNameFilter,
      effectiveDateGteFilter,
      effectiveDateLteFilter,
      lastQuoteDateGteFilter,
      lastQuoteDateLteFilter,
      lastQuoteStatusFilter,
      deboucedEmailFilter,
      stateFilter,
      agencyFilter,
      brokerFilter,
      officeFilter
    ],
    ({ pageLimit, pageNumber }) => {
      const params: IGroupQueryParams = {
        page_number: pageNumber,
        page_limit: pageLimit,
        ordering: ordering,
        groupName: groupNameFilter,
        effectiveDateGte: effectiveDateGteFilter,
        effectiveDateLte: effectiveDateLteFilter,
        lastQuoteDateGte: lastQuoteDateGteFilter,
        lastQuoteDateLte: lastQuoteDateLteFilter,
        lastQuoteStatus: lastQuoteStatusFilter,
        email: deboucedEmailFilter,
        state: stateFilter,
        agencyId: agencyFilter,
        brokerId: brokerFilter,
        officeId: officeFilter,
      }
      return quoteService.getGroups(params)
    },
    {
      showMax: 50
    }
  )

  const data = queryResult.data?.data
  const shouldShowBrokerInformation = queryResult.data?.tableType === 'group_and_broker'
  const numberOfColumns = shouldShowBrokerInformation ? 10 : 7
  // const isLoading = queryResult.isLoading

  const checkDataIsEmpty =
    data?.length === 0 &&
    !effectiveDateGteFilter &&
    !effectiveDateLteFilter &&
    !lastQuoteStatusFilter &&
    !groupNameFilter &&
    !emailFilter &&
    !stateFilter &&
    !lastQuoteDateGteFilter &&
    !lastQuoteDateLteFilter &&
    !agencyFilter &&
    !brokerFilter &&
    !officeFilter

  useEffect(() => {
    if (checkDataIsEmpty) {
      setShowFTUModal(true)
    }
  }, [data, checkDataIsEmpty])

  useEffect(() => {
    if (agencies && agencies.length === 1) {
      setAgencyFilter(agencies[0].entryId)
    }
  }, [agencies])

  useEffect(() => {
    if (brokers && brokers.length === 1) {
      setBrokerFilter(brokers[0].entryId)
    }
  }, [brokers])

  useEffect(() => {
    if (offices && offices.length === 1) {
      setOfficeFilter(offices[0].entryId)
    }
  }, [offices])

  const handleGroupSortClick = (sortBy: string) => {
    if (sortBy === ordering.column) {
      setOrdering((ordering) => ({
        ...ordering,
        direction: ordering.direction === 'asc' ? 'desc' : 'asc'
      }))
    } else {
      setOrdering((ordering) => ({
        ...ordering,
        column: sortBy
      }))
    }
  }

  // handles accordion expansion
  const handleRowClick = (index: number) => {
    if (expandedRow === index) {
      setExpandedRow(null)
      setQuotesSortBy(quoteEnums.quoteNumber)
      setQuotesSortDirection(sortDirectionEnums.desc)
    } else {
      setExpandedRow(index)
    }
  }

  const onQuoteNumberSortClick = () => {
    if (quotesSortBy !== quoteEnums.quoteNumber) {
      setQuotesSortBy(quoteEnums.quoteNumber)
      setQuotesSortDirection(sortDirectionEnums.desc)
    } else {
      if (quotesSortDirection === sortDirectionEnums.asc) {
        setQuotesSortDirection(sortDirectionEnums.desc)
      } else {
        setQuotesSortDirection(sortDirectionEnums.asc)
      }
    }
  }

  const onQuoteDateSortClick = () => {
    if (quotesSortBy !== quoteEnums.quoteDate) {
      setQuotesSortBy(quoteEnums.quoteDate)
      setQuotesSortDirection(sortDirectionEnums.desc)
    } else {
      if (quotesSortDirection === sortDirectionEnums.asc) {
        setQuotesSortDirection(sortDirectionEnums.desc)
      } else {
        setQuotesSortDirection(sortDirectionEnums.asc)
      }
    }
  }

  const getViewQuoteLabel = (status: string) => {
    if (status === 'draft') {
      return 'View Draft'
    }

    if (status === 'proposal') {
      return 'View Proposal'
    }

    return 'View Quote'
  }

  // returns the quote elements. This helper is for assisting in sorting
  const showQuotes = (quotes: IQuoteGroupDTO[]) => {
    let renderedQuotes = [...quotes]
    let sortDirectionMultiplier =
      quotesSortDirection === sortDirectionEnums.desc ? 1 : -1

    if (quotesSortBy === quoteEnums.quoteDate) {
      renderedQuotes.sort((a, b) => {
        if (a.updatedAt > b.updatedAt) {
          return -1 * sortDirectionMultiplier
        }
        if (a.updatedAt < b.updatedAt) {
          return 1 * sortDirectionMultiplier
        }

        return 0
      })
    } else if (quotesSortBy === quoteEnums.quoteNumber) {
      renderedQuotes.sort((a, b) => {
        if (a.entryId > b.entryId) {
          return -1 * sortDirectionMultiplier
        }
        if (a.entryId < b.entryId) {
          return 1 * sortDirectionMultiplier
        }

        return 0
      })
    }

    return renderedQuotes.map(
      (quoteDetailsRow: any, quoteDetailsIndex: any) => (
        <React.Fragment key={quoteDetailsIndex}>
          <tr>
            <td
              style={{
                border: 'none',
                borderBottom: '0.0625rem solid rgba(231, 234, 243, 0.7)',
                color: '#6D39D9',
                verticalAlign: 'middle'
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  width: 'min-content'
                }}
                onClick={() =>
                  onViewQuoteClick(
                    quoteDetailsRow.entryId,
                    quoteDetailsRow.status === 'draft'
                  )
                }
              >
                {quoteDetailsRow.entryId}
              </div>
            </td>
            <td
              style={{
                border: 'none',
                borderBottom: '0.0625rem solid rgba(231, 234, 243, 0.7)',
                verticalAlign: 'middle'
              }}
            >
              <span style={setQuoteStatusColor(quoteDetailsRow.status)} />
              {convertQuoteStatusToLabel(quoteDetailsRow.status)}
            </td>
            <td
              style={{
                border: 'none',
                borderBottom: '0.0625rem solid rgba(231, 234, 243, 0.7)',
                verticalAlign: 'middle'
              }}
            >
              {!!quoteDetailsRow.updatedAt &&
                new Date(quoteDetailsRow.updatedAt)
                  .toISOString()
                  .substring(0, 10)}
            </td>
            <td
              style={{
                border: 'none',
                borderBottom: '0.0625rem solid rgba(231, 234, 243, 0.7)',
                verticalAlign: 'middle'
              }}
            >
              {quoteDetailsRow.platformQuoted}
            </td>
            <td
              style={{
                border: 'none',
                borderBottom: '0.0625rem solid rgba(231, 234, 243, 0.7)',
                textAlign: 'right'
              }}
            >
              <DotsMenu className="border-light dropdown-menu-lg">
                <Dropdown.Item
                  className="py-2 pl-3 groupDropdownWidth"
                  onClick={() =>
                    onViewQuoteClick(
                      quoteDetailsRow.entryId,
                      quoteDetailsRow.status === 'draft'
                    )
                  }
                >
                  {getViewQuoteLabel(quoteDetailsRow.status)}
                </Dropdown.Item>
                {/* <Dropdown.Item
                    className="py-2 pl-3 text-danger"
                    onClick={() => {
                      console.log('TO DO - archive quote is not MVP')
                    }}
                  >
                    Archive Quote
                  </Dropdown.Item> */}
              </DotsMenu>
            </td>
          </tr>
        </React.Fragment>
      )
    )
  }

  // TODO: CSS - Replace temp filter button with the mock one, complete with the icon

  const onFilterBtnClick = () => {
    setShowFilterFormPopover(!showFilterFormPopover)
  }

  const onFilterCloseBtnClick = () => {
    setShowFilterFormPopover(!showFilterFormPopover)
  }

  const onFilterResetBtnClick = () => {
    setEffectiveDateGteFilter(null)
    setEffectiveDateLteFilter(null)
    setLastQuoteDateGteFilter(null)
    setLastQuoteDateLteFilter(null)
    setLastQuoteStatusFilter('')
    setEmailFilter('')
    setStateFilter('')
    setAgencyFilter(undefined)
    setBrokerFilter(undefined)
    setOfficeFilter(undefined)
  }

  const setQuoteStatusColor = (quoteStatus: string) => {
    let color = '#DC3545'

    if (quoteStatus === 'draft') {
      color = '#EDCA60'
    } else if (quoteStatus === 'in_review') {
      color = '#6D39D9'
    } else if (quoteStatus === 'proposal') {
      color = '#8E9EFF'
    } else if (quoteStatus === 'sold') {
      color = '#7DD491'
    } else if (quoteStatus === 'archived') {
      color = '#6C757D'
    } else if (quoteStatus === 'quoted') {
      color = '#17A2B8'
    }

    return { ...quoteStatusStyles, backgroundColor: color }
  }

  const convertQuoteStatusToLabel = (quoteStatus: string) => {
    let label = 'Declined'

    if (quoteStatus === 'draft') {
      label = 'Draft'
    } else if (quoteStatus === 'in_review') {
      label = 'In Review'
    } else if (quoteStatus === 'proposal') {
      label = 'Proposal'
    } else if (quoteStatus === 'sold') {
      label = 'Sold'
    } else if (quoteStatus === 'archived') {
      label = 'Archived'
    } else if (quoteStatus === 'quoted') {
      label = 'Quoted'
    } else if (quoteStatus === 'error') {
      label = 'Error'
    }

    return label
  }

  const popoverFilterForm = (
    <Popover
      id="popover-basic"
      className="border shadow p-3 filter-popover-width"
      style={{ minWidth: 560 }}
    >
      <div className="container-flex">
        <div className="row no-gutters mx-2">
          <div className="col-12 col-sm-6">
            <Form.Group>
              <Form.Label>Effective Date</Form.Label>
              <DatePicker
                selected={effectiveDateGteFilter}
                onChange={(date) => setEffectiveDateGteFilter(date)}
                selectsStart
                startDate={effectiveDateGteFilter}
                endDate={effectiveDateLteFilter}
                placeholderText="Start Date"
              />
              <DatePicker
                className="datepicker-marginTop"
                selected={effectiveDateLteFilter}
                onChange={(date) => setEffectiveDateLteFilter(date)}
                selectsEnd
                startDate={effectiveDateGteFilter}
                endDate={effectiveDateLteFilter}
                minDate={effectiveDateGteFilter}
                placeholderText="End Date"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Quote Status</Form.Label>
              <Form.Control
                as="select"
                onChange={(e) => setLastQuoteStatusFilter(e.target.value)}
                placeholder=""
                className="bg-white"
                value={lastQuoteStatusFilter}
              >
                <option value="">All</option>
                <option value="quoted">Quoted</option>
                <option value="draft"> Draft</option>
                <option value="in_review"> In Review</option>
                <option value="proposal">Proposal</option>
                <option value="sold">Sold</option>
                <option value="archived">Archived</option>
                <option value="declined">Declined</option>
              </Form.Control>
            </Form.Group>
            { agencies?.length !== 0 && (<Form.Group>
              <Form.Label>{filterEnums.agencyName}</Form.Label>
              <Form.Control
                as="select"
                onChange={(e) => setAgencyFilter(parseInt(e.target.value))}
                placeholder=""
                className="bg-white"
                value={agencyFilter}
              >
                {[{ entryId: undefined, name: '' }, ...(agencies || [])].map((agency) => (
                    <option value={agency.entryId} key={`agency-key-${agency.entryId}`}>
                      {agency.name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>)}
            { brokers?.length !== 0 && (<Form.Group>
              <Form.Label>{filterEnums.brokerName}</Form.Label>
              <Form.Control
                as="select"
                onChange={(e) => setBrokerFilter(parseInt(e.target.value))}
                placeholder=""
                className="bg-white"
                value={brokerFilter}
              >
                {[{ entryId: undefined, fullName: '' }, ...(brokers || [])].map((broker) => (
                    <option value={broker.entryId} key={`broker-key-${broker.entryId}`}>
                      {broker.fullName}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>)}
          </div>
          <div className="col-12 col-sm-6">
            <div className="mx-2">
              <Form.Group className="">
                <Form.Label>Last Quote Date</Form.Label>
                <DatePicker
                  selected={lastQuoteDateGteFilter}
                  onChange={(date) => setLastQuoteDateGteFilter(date)}
                  selectsStart
                  startDate={lastQuoteDateGteFilter}
                  endDate={lastQuoteDateLteFilter}
                  placeholderText="Start Date"
                />
                <DatePicker
                  className="datepicker-marginTop"
                  selected={lastQuoteDateLteFilter}
                  onChange={(date) => setLastQuoteDateLteFilter(date)}
                  selectsEnd
                  startDate={lastQuoteDateGteFilter}
                  endDate={lastQuoteDateLteFilter}
                  minDate={lastQuoteDateGteFilter}
                  placeholderText="End Date"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Group State</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(e) => setStateFilter(e.target.value)}
                  placeholder=""
                  className="bg-white"
                  value={stateFilter}
                >
                  {['All', ...(options?.states || [])].map((state) => (
                    <option value={state} key={state}>
                      {state}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              { offices?.length !== 0 && (<Form.Group>
                <Form.Label>{filterEnums.agencyOffice}</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(e) => setOfficeFilter(parseInt(e.target.value))}
                  placeholder=""
                  className="bg-white"
                  value={officeFilter}
                >
                  {[{ entryId: undefined, name: '' }, ...(offices || [])].map((office) => (
                      <option value={office.entryId} key={`office-key-${office.entryId}`}>
                        {office.name}
                      </option>
                    ))}
                </Form.Control>
            </Form.Group>)}
            </div>
          </div>
        </div>
        <div className="row no-gutters d-flex flex-row justify-content-end mx-2">
          <Button
            variant="outline-primary"
            onClick={onFilterResetBtnClick}
            className="mr-2"
          >
            Reset
          </Button>
          <Button type="submit" onClick={onFilterCloseBtnClick}>
            Close
          </Button>
        </div>
      </div>
    </Popover>
  )

  const FormModal = () => {
    return (
      <>
        <Modal
          show={showFTUModal}
          centered
          onHide={() => setShowFTUModal(false)}
          dialogClassName="benefitsBuilderFtuxModalDialog"
          className="benefitsBuilderFtuxModal"
          style={{
            zIndex: 2000
          }}
        >
          <div className="imgContainer">
            <img src={FTUimg} className="ftuImg" alt="filter" />
          </div>
          <Modal.Body>
            <Row
              style={{
                fontSize: '22px',
                fontWeight: 600,
                justifyContent: 'center'
              }}
            >
              <div style={{ display: 'flex' }}>
                Welcome to the Angle Benefit Builder
                <Badge
                  className="ml-2"
                  style={{
                    alignSelf: 'center',
                    backgroundColor: '#0099AE',
                    height: '20px',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 500
                  }}
                >
                  BETA
                </Badge>
              </div>
            </Row>
            <Row
              style={{
                color: '#717171',
                fontWeight: 500,
                marginLeft: '20px',
                textAlign: 'center',
                width: '568px'
              }}
            >
              You've been given early access to Benefit Builder. Tailor your
              clients' network, PBM, and cost shares to their employee
              populations' specific needs while enabling fully integrated point
              solutions. Benefit Builder is your complete benefits solution. Get
              started below!
            </Row>
            <Row style={{ justifyContent: 'center', marginTop: '20px' }}>
              <Button onClick={onNewQuoteClick}>Create A Quote</Button>
            </Row>
          </Modal.Body>
        </Modal>
        <GroupFormModal
          key={JSON.stringify(isGroupFormModalOpen)}
          open={isGroupFormModalOpen}
          onClose={() => setIsGroupFormModalOpen(false)}
          groupIdToEdit={groupIdToEdit}
        />
      </>
    )
  }

  return (
    <Layout showUserDropdown>
      <FormModal />
      <div
        className="container-fluid px-md-10 px-3 mb-4"
        style={{ background: '#F2F5F9', paddingTop: 50, paddingBottom: 50 }}
      >
        <div className="row no-gutters">
          <h1>Benefit Builder</h1>
        </div>
        <div className="row no-gutters">
          <p className="fw-semibold fs-6 font-color-gray">
            Flexible quoting on all of your Angle business. Start a new quote or
            add additional quotes to a current lead
          </p>
        </div>

        <div className="row no-gutters">
          <div className="col-sm-4 col-6">
            <Form.Group>
              <Form.Control
                placeholder="Search by group name"
                onChange={(e) => setGroupNameFilter(e.target.value)}
                style={{
                  border: '0.0625rem solid #E7EAF3',
                  height: '48px',
                  backgroundColor: '#fff'
                }}
              />
            </Form.Group>
          </div>

          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={popoverFilterForm}
            trigger="click"
            show={showFilterFormPopover}
          >
            <Button
              className="ml-2 bg-white text-secondary d-flex flex-row align-items-center"
              onClick={onFilterBtnClick}
              style={{
                border: '0.0625rem solid #E7EAF3',
                height: '48px'
              }}
            >
              <img src={filterIcon} className="h-24 mr-2" alt="filter" />
              <span className="d-none d-sm-block">Filters</span>
              <i
                className="icon fs-10 icon-dropdown ml-4"
                style={{
                  height: '20px',
                  width: '20px',
                  paddingLeft: '5px'
                }}
              />
            </Button>
          </OverlayTrigger>
          <div className="ml-auto text-nowrap pl-2">
            <Button
              onClick={onNewQuoteClick}
              style={{
                border: '0.0625rem solid #E7EAF3'
              }}
            >
              <span className="d-none d-md-block">New Quote</span>
              <i className="fs-16 icon-plus d-sm-none" />
            </Button>
          </div>
        </div>
      </div>
      <Container
        fluid
        style={{ paddingTop: 50, paddingBottom: 100, marginTop: -90 }}
      >
        <Table bordered hover>
          <thead style={{ backgroundColor: '#F8F9FA' }}>
            <tr>
              <th
                style={{ border: 'none', paddingLeft: '60px' }}
                onClick={() => handleGroupSortClick('groupName')}
              >
                {groupEnums.groupName}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              <th
                style={{ border: 'none' }}
                onClick={() => handleGroupSortClick('effectiveDate')}
              >
                {groupEnums.effectiveDate}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              <th
                style={{ border: 'none' }}
                onClick={() => handleGroupSortClick('quoteCount')}
              >
                {groupEnums.quotes}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              <th
                style={{ border: 'none' }}
                onClick={() => handleGroupSortClick('members')}
              >
                {groupEnums.members}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              <th
                style={{ border: 'none' }}
                onClick={() => handleGroupSortClick('lastQuoteDate')}
              >
                {groupEnums.lastQuoted}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              <th
                style={{ border: 'none' }}
                onClick={() => handleGroupSortClick('lastQuoteStatus')}
              >
                {groupEnums.quoteStatus}
                <img src={sortIcon} className="sortIcon" alt="sort" />
              </th>
              {
                shouldShowBrokerInformation ? (
                  <>
                    <th style={{ border: 'none' }}>
                      {groupEnums.brokerName}
                    </th>
                    <th style={{ border: 'none' }}>
                      {groupEnums.agencyName}
                    </th>
                    <th style={{ border: 'none' }}>
                      {groupEnums.agencyOffice}
                    </th>
                  </>
                ) : null
              }
              <th style={{ border: 'none' }}>{''}</th>
            </tr>
          </thead>
          <tbody style={{ color: '#6C757D' }}>
            {checkDataIsEmpty && (
              <tr>
                <td colSpan={numberOfColumns} className="text-center p-8">
                  {/* <div className="p-10 w-100"> */}
                  <h4 className="text-muted">
                    <i>Create Your First Quote</i>
                  </h4>
                  <Button onClick={onNewQuoteClick}>New Quote</Button>
                  {/* </div> */}
                </td>
              </tr>
            )}
            {data?.map(
              (row: any, index: any) =>
                row?.quotes?.length > 0 && (
                  <React.Fragment key={index}>
                    <tr
                      onClick={() => handleRowClick(index)}
                      className={expandedRow === index ? 'expanded' : ''}
                    >
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          color: '#212529',
                          fontWeight: '500',
                          display: 'flex'
                        }}
                      >
                        <i
                          className={
                            expandedRow === index
                              ? 'icon icon-down-arrow text-primary'
                              : 'icon icon-right-arrow text-primary'
                          }
                        />
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {row?.groupName}
                          <span style={{ color: '#6C757D', fontWeight: '400' }}>
                            {row?.city}, {row?.state}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          verticalAlign: 'middle'
                        }}
                      >
                        {row?.effectiveDate}
                      </td>
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          verticalAlign: 'middle'
                        }}
                      >
                        {row.quotes?.length}
                      </td>
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          verticalAlign: 'middle'
                        }}
                      >
                        {row?.members}
                      </td>
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          verticalAlign: 'middle'
                        }}
                      >
                        {!!row.quotes?.[0]?.updatedAt &&
                          new Date(row.quotes?.[0]?.updatedAt)
                            .toISOString()
                            .substring(0, 10)}
                      </td>
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          verticalAlign: 'middle'
                        }}
                      >
                        <span
                          style={setQuoteStatusColor(row.quotes?.[0]?.status)}
                        />
                        {convertQuoteStatusToLabel(row.quotes?.[0]?.status)}
                      </td>
                      {
                        shouldShowBrokerInformation ? (
                          <>
                            <td
                              style={{
                                border: 'none',
                                borderBottom:
                                  '0.0625rem solid rgba(231, 234, 243, 0.7)',
                                verticalAlign: 'middle'
                              }}
                            >
                              {row?.broker?.name}
                            </td>
                            <td
                              style={{
                                border: 'none',
                                borderBottom:
                                  '0.0625rem solid rgba(231, 234, 243, 0.7)',
                                verticalAlign: 'middle'
                              }}
                            >
                              {row?.broker?.agency}
                            </td>
                            <td
                              style={{
                                border: 'none',
                                borderBottom:
                                  '0.0625rem solid rgba(231, 234, 243, 0.7)',
                                verticalAlign: 'middle'
                              }}
                            >
                              {row?.broker?.office}
                            </td>
                          </>
                        ) : null
                      }
                      <td
                        style={{
                          border: 'none',
                          borderBottom:
                            '0.0625rem solid rgba(231, 234, 243, 0.7)',
                          textAlign: 'right'
                        }}
                      >
                        <DotsMenu className="border-light dropdown-menu-lg">
                          <Dropdown.Item
                            className="py-2 pl-3 groupDropdownWidth"
                            onClick={() => {
                              setGroupIdToEdit(row.entryId)
                              setIsGroupFormModalOpen(true)
                            }}
                          >
                            Edit Group
                          </Dropdown.Item>
                        </DotsMenu>
                      </td>
                    </tr>
                    {expandedRow === index && (
                      <tr>
                        <td colSpan={numberOfColumns}>
                          <Accordion>
                            <Table bordered hover>
                              <thead style={{ backgroundColor: '#F8F9FA' }}>
                                <tr>
                                  <th
                                    style={{ border: 'none' }}
                                    onClick={onQuoteNumberSortClick}
                                  >
                                    {quoteEnums.quoteNumber}
                                    <img
                                      src={sortIcon}
                                      className="sortIcon"
                                      alt="sort"
                                    />
                                  </th>
                                  <th style={{ border: 'none' }}>
                                    {'QUOTE STATUS'}
                                  </th>
                                  <th
                                    style={{ border: 'none' }}
                                    onClick={onQuoteDateSortClick}
                                  >
                                    {quoteEnums.quoteDate}
                                    <img
                                      src={sortIcon}
                                      className="sortIcon"
                                      alt="sort"
                                    />
                                  </th>
                                  <th style={{ border: 'none' }}>
                                    {'PLATFORM QUOTED'}
                                  </th>
                                  <th style={{ border: 'none' }}>{''}</th>
                                </tr>
                              </thead>
                              <tbody style={{ color: '#6C757D' }}>
                                {showQuotes(row.quotes)}
                              </tbody>
                            </Table>
                          </Accordion>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
            )}
            <tr>
              <td colSpan={numberOfColumns} style={{ paddingLeft: '26px' }}>
                <Pagination
                  {...paginationConfig}
                  pageNumberList={[50, 100, 250]}
                />
              </td>
            </tr>
          </tbody>
        </Table>
      </Container>
    </Layout>
  )
}

export default Quotes
